import React, { useState, useEffect } from 'react';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { MOB_REGISTRY } from '../../../../shared/MobRegistry';

interface NPCToolsTabProps {
    onAction: (action: string, data?: any) => void;
}

export const NPCToolsTab: React.FC<NPCToolsTabProps> = ({ onAction }) => {
  // Live Mob List State
  const [activeMobs, setActiveMobs] = useState<any[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const mobManager = ServiceRegistry.getInstance().get('MobSystem', { optional: true })?.getManager();
        if (mobManager) {
          const mobs = mobManager.getAllMobs();
          // Get player position for distance calculation
          const entitySystem = ServiceRegistry.getInstance().get('EntitySystem');
          const player = entitySystem?.getPlayerManager()?.getPlayer();
          const playerWidth = player?.width || 40;
          const playerHeight = player?.height || 60;
          const px = player ? (player.position?.x || 0) + playerWidth / 2 : 0;
          const py = player ? (player.position?.y || 0) + playerHeight / 2 : 0;

          const mobData = mobs.map((mob: any) => {
            const dist = player ? Math.round(Math.sqrt(
              Math.pow(mob.x - px, 2) + Math.pow(mob.y - py, 2)
            )) : 0;
            return {
              id: mob.id,
              name: mob.enemyName || mob.name || 'Unknown',
              x: Math.round(mob.x),
              y: Math.round(mob.y),
              hp: Math.round(mob.health),
              maxHp: Math.round(mob.maxHealth),
              dist
            };
          });
          // Sort by distance (nearest first)
          mobData.sort((a: any, b: any) => a.dist - b.dist);
          setActiveMobs(mobData);
        }
      } catch {
        // Ignore errors if system not ready
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const mobTypes = Object.keys(MOB_REGISTRY).filter(id => id !== 'pig'); // Exclude legacy pig

  const buttonStyle: React.CSSProperties = {
    padding: '8px 12px',
    margin: '5px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    color: 'white',
    cursor: 'pointer',
    fontFamily: '\'Inter\', sans-serif',
    flex: '1 0 40%',
    fontSize: '12px'
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '20px',
    background: 'rgba(0,0,0,0.2)',
    padding: '10px',
    borderRadius: '8px'
  };

  const headerStyle: React.CSSProperties = {
    margin: '0 0 10px 0',
    fontSize: '13px',
    color: '#aaa',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    letterSpacing: '0.5px'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
      <div style={sectionStyle}>
        <h3 style={headerStyle}>Active NPCs ({activeMobs.length})</h3>
        <div style={{ maxHeight: '200px', overflowY: 'auto', background: 'rgba(0,0,0,0.3)', padding: '5px' }}>
          <table style={{ width: '100%', fontSize: '11px', color: '#ccc', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #444', color: '#888' }}>
                <th style={{ padding: '4px' }}>Name</th>
                <th style={{ padding: '4px' }}>Dist</th>
                <th style={{ padding: '4px' }}>Coords</th>
                <th style={{ padding: '4px' }}>HP</th>
                <th style={{ padding: '4px' }}>ID</th>
              </tr>
            </thead>
            <tbody>
              {activeMobs.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: '10px', textAlign: 'center', color: '#666' }}>No Mobs Detected</td></tr>
              ) : (
                activeMobs.map(mob => (
                  <tr key={mob.id} style={{ borderBottom: '1px solid #333' }}>
                    <td style={{ padding: '4px', fontWeight: 'bold' }}>{mob.name}</td>
                    <td style={{ padding: '4px', fontFamily: 'monospace', color: mob.dist <= 200 ? '#4f4' : mob.dist <= 400 ? '#ff4' : '#f44' }}>{mob.dist}p</td>
                    <td style={{ padding: '4px', fontFamily: 'monospace' }}>{mob.x},{mob.y}</td>
                    <td style={{ padding: '4px' }}>{mob.hp}/{mob.maxHp}</td>
                    <td style={{ padding: '4px', fontSize: '9px', opacity: 0.6, fontFamily: 'monospace' }}>{mob.id}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={sectionStyle}>
        <h3 style={headerStyle}>Server Spawn (Persistent)</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {mobTypes.map(mobId => (
            <button
              key={mobId}
              style={buttonStyle}
              onClick={() => onAction('spawn', mobId)}
            >
              Spawn {MOB_REGISTRY[mobId]?.name || mobId}
            </button>
          ))}
        </div>
      </div>

      <div style={sectionStyle}>
        <h3 style={headerStyle}>Map Actions</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          <button
            style={{ ...buttonStyle, flex: 1, margin: 0, background: 'rgba(239, 68, 68, 0.2)', borderColor: 'rgba(239, 68, 68, 0.4)' }}
            onClick={() => onAction('clear_mobs')}
          >
            🗑️ Clear All Mobs (Server Map)
          </button>
        </div>
      </div>

      <div style={sectionStyle}>
        <h3 style={headerStyle}>Debug Tools</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          <button
            style={{ ...buttonStyle, flex: 1, margin: 0 }}
            onClick={() => onAction('toggle_hitboxes')}
          >
            📦 Toggle Hitboxes
          </button>
          <button
            style={{ ...buttonStyle, flex: 1, margin: 0 }}
            onClick={() => onAction('toggle_velocity')}
          >
            ➡️ Show Velocity
          </button>
          <button
            style={{ ...buttonStyle, flex: 1, margin: 0 }}
            onClick={() => onAction('toggle_aggro_range')}
          >
            🎯 Aggro Range
          </button>
        </div>
      </div>
    </div>
  );
};
